import React from "react";
import { useUserForm } from "./hooks/useUserForm";
import { Form, Formik } from "formik";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  DialogActions,
  FormControlLabel,
  TextField,
} from "@mui/material";

function UserForm({ editMode, currentUser, setOpen, handleClose }) {
  const {
    validationSchema,
    initialValues,
    handleSubmit,
    organisationSearchResults,
    setOrganisationSearchTerm,
    roles,
  } = useUserForm({
    editMode,
    currentUser,
    setOpen,
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        isSubmitting,
        getFieldProps,
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
      }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <Box sx={{ p: 3 }}>
              <TextField
                fullWidth
                id="firstName"
                name="firstName"
                label="First Name"
                variant="outlined"
                margin="normal"
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                {...getFieldProps("firstName")}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
              />

              <TextField
                fullWidth
                id="lastName"
                name="lastName"
                label="Last Name"
                variant="outlined"
                margin="normal"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                {...getFieldProps("lastName")}
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
              />

              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                variant="outlined"
                margin="normal"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                {...getFieldProps("email")}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />

              <Autocomplete
                // map roles to get the name of the role
                multiple
                options={roles}
                getOptionLabel={(option) => option.name}
                fullWidth
                noOptionsText="No roles available"
                value={values.roles || []}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                onChange={(event, newValue) => {
                  setFieldValue("roles", newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Role"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.roles && Boolean(errors.roles)}
                    helperText={touched.roles && errors.roles}
                  />
                )}
              />

              <Autocomplete
                options={organisationSearchResults}
                getOptionLabel={(option) => option.name}
                noOptionsText="No Organisation found"
                value={values.organisation || null} // Align with Formik field name
                onInputChange={(event, newInputValue) =>
                  setOrganisationSearchTerm(newInputValue)
                }
                onChange={
                  (event, newValue) => setFieldValue("organisation", newValue) // Update 'organisation' field
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organisation"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.organisation && Boolean(errors.organisation)}
                    helperText={touched.organisation && errors.organisation}
                  />
                )}
              />

              {!editMode && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.sendUserWelcomeEmail}
                      onChange={handleChange}
                      name="sendUserWelcomeEmail"
                      color="primary"
                    />
                  }
                  label="Send welcome email with log-in details"
                />
              )}

              <DialogActions>
                <Button onClick={handleClose} color="primary">
                  Cancel
                </Button>
                <Button type="submit" color="primary" disabled={isSubmitting}>
                  {editMode ? "Update" : "Create"}
                </Button>
              </DialogActions>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

export default UserForm;
